import {
  PayoutHoldReason,
  PayoutStatus,
  SellerAccountStatus,
} from "@/Apollo/schema";
import { matchesAccountStatusFilter } from "@/components/Account/common/hooks/useAccountStatusFilter";
import {
  ACTION_NEEDED_PAYOUT_STATUSES,
  PAID_PAYOUT_STATUSES,
  SELLER_SALE_STATUS,
  SellerSaleStatus,
} from "@/components/Account/sales/sales.static";

/** The sale's amounts as the gateway sends them (the seller's `Order`). */
export type SalePayoutAmounts = {
  subtotal_incl_vat?: number | null;
  fee_incl_vat?: number | null;
  fee_excl_vat?: number | null;
  feeVatAmount?: { amount: number } | null;
  commissionPercent?: number | null;
  payout?: number | null;
};

export type PayoutBreakdown = {
  salePrice: number;
  /** Ex VAT when `feeVat` is set, VAT-inclusive otherwise. */
  commission: number;
  /** The VAT charged on the commission; null when the sale charged none. */
  feeVat: number | null;
  payout: number;
  /** The rate the sale was charged; null for a fixed fee. */
  commissionPercent: number | null;
};

export const isExpiredPayoutStatus = (
  payoutStatus?: PayoutStatus | null
): boolean => payoutStatus === PayoutStatus.Expired;

/**
 * Whether the seller's account holds payouts back. With the gateway's
 * `account_status` (the one derivation dashboard, My sales and Payment
 * settings share) that is the blocked statuses, plus a review while payouts
 * are off; without it, the legacy rule on the two booleans.
 */
export const isPayoutsBlocked = (
  accountCreated?: boolean | null,
  payoutsEnabled?: boolean | null,
  accountStatus?: SellerAccountStatus | null
): boolean => {
  if (accountStatus) {
    return (
      accountStatus === SellerAccountStatus.BlockedActionNeeded ||
      accountStatus === SellerAccountStatus.BlockedNotFixable ||
      (accountStatus === SellerAccountStatus.UnderReview && !payoutsEnabled)
    );
  }

  return !!accountCreated && !payoutsEnabled;
};

/** What the gateway says a payout waits for, when this build asked for it. */
export type SaleHoldInfo = {
  holdReason?: PayoutHoldReason | null;
  /** True only once Stripe's payout.paid webhook confirmed the money arrived. */
  paidConfirmed?: boolean | null;
};

/**
 * A completed order whose Stripe transfer has not been made. With the
 * gateway's `hold_reason` the answer is exact (ACCOUNT); without it, the
 * legacy reading: `Order.payout_status` PENDING_TRANSFER on a blocked
 * account. The gateway never puts PENDING_TRANSFER on `payoutDetails.status`.
 */
export const isHeldPayout = (
  orderPayoutStatus: PayoutStatus | null | undefined,
  payoutsBlocked: boolean,
  holdReason?: PayoutHoldReason | null
): boolean => {
  if (holdReason) {
    return holdReason === PayoutHoldReason.Account;
  }

  return orderPayoutStatus === PayoutStatus.PendingTransfer && payoutsBlocked;
};

export const getSellerSaleStatus = (
  payoutDetailsStatus: PayoutStatus | null | undefined,
  payoutsBlocked: boolean,
  orderPayoutStatus?: PayoutStatus | null,
  hold?: SaleHoldInfo
): SellerSaleStatus | null => {
  if (
    isExpiredPayoutStatus(payoutDetailsStatus) ||
    isExpiredPayoutStatus(orderPayoutStatus)
  ) {
    return null;
  }

  if (
    payoutDetailsStatus === PayoutStatus.Canceled ||
    orderPayoutStatus === PayoutStatus.Canceled
  ) {
    return SELLER_SALE_STATUS.canceled;
  }

  if (
    (payoutDetailsStatus &&
      ACTION_NEEDED_PAYOUT_STATUSES.includes(payoutDetailsStatus)) ||
    (orderPayoutStatus &&
      ACTION_NEEDED_PAYOUT_STATUSES.includes(orderPayoutStatus))
  ) {
    return SELLER_SALE_STATUS.actionNeeded;
  }

  if (
    (payoutDetailsStatus &&
      PAID_PAYOUT_STATUSES.includes(payoutDetailsStatus)) ||
    (orderPayoutStatus && PAID_PAYOUT_STATUSES.includes(orderPayoutStatus))
  ) {
    // SUCCEEDED is calendar arithmetic in the monolith, not Stripe's word.
    // Unconfirmed while the account is blocked means the money sits in the
    // seller's Stripe balance: that is Actie nodig, not Uitbetaald. With
    // payouts on, an unconfirmed payout is history from before the webhook
    // wrote the column and stays Uitbetaald.
    if (payoutsBlocked && hold?.paidConfirmed === false) {
      return SELLER_SALE_STATUS.actionNeeded;
    }

    return SELLER_SALE_STATUS.paid;
  }

  // The account-level Stripe block is said once, in the banner. Rows keep
  // their own state; the only per-order escalation is a payout that is ready
  // to transfer but held by the blocked account.
  if (isHeldPayout(orderPayoutStatus, payoutsBlocked, hold?.holdReason)) {
    return SELLER_SALE_STATUS.actionNeeded;
  }

  return SELLER_SALE_STATUS.pending;
};

export type SaleActionNeededCause = "failed" | "disputed" | "held";

/**
 * Which of the three bundled Actie nodig causes an order carries. Both
 * payout fields are read, matching `getSellerSaleStatus`; a payout that is
 * neither FAILED nor DISPUTED is the one held by the unverified account.
 */
export const getSaleActionNeededCause = (
  order: {
    payoutDetails?: {
      status?: PayoutStatus | null;
      hold_reason?: PayoutHoldReason | null;
    } | null;
    payout_status?: PayoutStatus | null;
  },
  status: SellerSaleStatus
): SaleActionNeededCause | null => {
  if (status !== SELLER_SALE_STATUS.actionNeeded) {
    return null;
  }

  const statuses = [order.payoutDetails?.status, order.payout_status];

  if (statuses.includes(PayoutStatus.Failed)) {
    return "failed";
  }

  if (
    statuses.includes(PayoutStatus.Disputed) ||
    order.payoutDetails?.hold_reason === PayoutHoldReason.Dispute
  ) {
    return "disputed";
  }

  return "held";
};

export const matchesSalesStatusFilter = (
  status: SellerSaleStatus,
  activeStatuses: readonly SellerSaleStatus[]
): boolean => matchesAccountStatusFilter(status, activeStatuses);

/**
 * The sale's payout breakdown, read from the gateway and never worked out
 * here (WHO-5991, deviation G). The VAT line is the gateway's
 * `feeVatAmount`, and shows only when the sale charged VAT on the
 * commission: the seller type doesn't decide it, since a business seller
 * with `charge_vat_on_fees` off pays none (WHO-6165). The monolith stores
 * `fee_excl_vat` even then, so it is shown only next to a VAT line.
 */
export const getPayoutBreakdown = (
  amounts: SalePayoutAmounts
): PayoutBreakdown => {
  const feeVat = amounts.feeVatAmount?.amount ?? 0;
  const chargesVat = feeVat > 0;

  return {
    salePrice: amounts.subtotal_incl_vat ?? 0,
    commission: (chargesVat ? amounts.fee_excl_vat : amounts.fee_incl_vat) ?? 0,
    feeVat: chargesVat ? feeVat : null,
    payout: amounts.payout ?? 0,
    commissionPercent: amounts.commissionPercent ?? null,
  };
};

export const sumPayoutForStatuses = (
  entries: { payout?: number | null; status: SellerSaleStatus | null }[],
  statuses: SellerSaleStatus[]
): number => {
  let total = 0;

  for (const entry of entries) {
    if (!entry.status || !statuses.includes(entry.status)) {
      continue;
    }

    total += entry.payout ?? 0;
  }

  return total;
};

/**
 * The My sales line: payouts waiting for the ACCOUNT, plus FAILED. Hidden
 * when 0. Disputes, returns, damage claims and refunds do not count; the
 * Stripe balance appears only on Payment settings, where the explanation
 * lives (fixed decision, spec v3).
 */
export const getHeldPayoutTotal = (
  entries: {
    payout?: number | null;
    payoutDetailsStatus?: PayoutStatus | null;
    orderPayoutStatus?: PayoutStatus | null;
    holdReason?: PayoutHoldReason | null;
  }[],
  payoutsBlocked: boolean
): number => {
  let total = 0;

  for (const entry of entries) {
    if (entry.payoutDetailsStatus === PayoutStatus.Failed) {
      total += entry.payout ?? 0;
      continue;
    }

    if (
      isHeldPayout(entry.orderPayoutStatus, payoutsBlocked, entry.holdReason)
    ) {
      total += entry.payout ?? 0;
    }
  }

  return Math.round(total * 100) / 100;
};
